import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useDiscoveryClient, useOptionalDiscoveryClient } from '../api/context';
import type { DiscoveryClient } from '../api/client';
import { ApiError, type ResolvedSound, type SoundInfo } from '../api/types';
import { soundKey, uniqueSoundRefs, type SoundRef } from '../lib/sounds';

export interface SoundLookup {
  status: 'idle' | 'loading' | 'ok' | 'error';
  sound: ResolvedSound | null;
  error: string | null;
}

/**
 * Resolve whatever is in the sound box, as it is typed or pasted.
 *
 * Debounced rather than fired per keystroke — each resolve can cost a redirect
 * plus a RapidAPI call, and someone typing an id by hand would otherwise spend
 * one per character.
 *
 * Every resolve is sequence-checked before it lands. Paste a link, then paste
 * a different one, and the two requests can return out of order; without the
 * guard the box would confirm the wrong sound, which is the exact failure this
 * feature exists to prevent.
 */
export function useSoundLookup(input: string, enabled: boolean): SoundLookup {
  const client = useDiscoveryClient();
  const [state, setState] = useState<SoundLookup>({ status: 'idle', sound: null, error: null });

  useEffect(() => {
    const raw = input.trim();
    if (!enabled || !raw) {
      setState({ status: 'idle', sound: null, error: null });
      return;
    }

    let live = true;
    setState((s) => ({ ...s, status: 'loading' }));
    const timer = setTimeout(() => {
      client
        .resolveSound(raw)
        .then((sound) => { if (live) setState({ status: 'ok', sound, error: null }); })
        .catch((err: Error) => {
          if (live) setState({ status: 'error', sound: null, error: err.message });
        });
    }, 500);

    return () => { live = false; clearTimeout(timer); };
  }, [input, enabled, client]);

  return state;
}

/* ─────────────────────────── the sound catalogue ─────────────────────────── */

/** Most sounds the lookup endpoint takes in one request. */
export const SOUND_LOOKUP_BATCH = 100;

type Waiter = { ref: SoundRef; resolve: (v: SoundInfo | null) => void; reject: (e: unknown) => void };

/**
 * Collects every sound asked for in the same tick into one request, so a
 * table of fifty rows that each show a sound costs one lookup, not fifty.
 * One per client; remembers when the server has no catalogue (an older
 * deploy) and stops asking.
 */
class SoundLoader {
  private waiting: Waiter[] = [];
  private scheduled = false;
  private unsupported = false;

  constructor(private readonly client: DiscoveryClient) {}

  load(ref: SoundRef): Promise<SoundInfo | null> {
    if (this.unsupported) return Promise.resolve(null);
    return new Promise((resolve, reject) => {
      this.waiting.push({ ref, resolve, reject });
      if (!this.scheduled) {
        this.scheduled = true;
        setTimeout(() => void this.flush(), 0);
      }
    });
  }

  private async flush(): Promise<void> {
    this.scheduled = false;
    const batch = this.waiting.splice(0);
    const refs = uniqueSoundRefs(batch.map((w) => w.ref));
    for (let i = 0; i < refs.length; i += SOUND_LOOKUP_BATCH) {
      const chunk = refs.slice(i, i + SOUND_LOOKUP_BATCH);
      const keys = new Set(chunk.map(soundKey));
      const mine = batch.filter((w) => keys.has(soundKey(w.ref)));
      try {
        const found = new Map((await this.client.sounds.lookup(chunk)).map((s) => [soundKey(s), s]));
        for (const w of mine) w.resolve(found.get(soundKey(w.ref)) ?? null);
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
          // No catalogue on this server: id-only links from now on.
          this.unsupported = true;
          for (const w of mine) w.resolve(null);
        } else {
          for (const w of mine) w.reject(err);
        }
      }
    }
  }
}

const loaders = new WeakMap<DiscoveryClient, SoundLoader>();
function loaderFor(client: DiscoveryClient): SoundLoader {
  let l = loaders.get(client);
  if (!l) {
    l = new SoundLoader(client);
    loaders.set(client, l);
  }
  return l;
}

/**
 * Names, covers and preview URLs for these sounds, from the server's sound
 * catalogue. Cached per sound; every sound requested in the same tick goes in
 * one request. Instagram has no metadata source, so only TikTok is asked.
 * Outside the Discovery tree (no client) nothing is fetched and the map is
 * empty — callers fall back to id-only links.
 */
export function useSounds(refs: readonly SoundRef[]): { byKey: ReadonlyMap<string, SoundInfo>; loading: boolean } {
  const client = useOptionalDiscoveryClient();
  const wanted = useMemo(() => uniqueSoundRefs(refs), [refs]);
  const results = useQueries({
    queries: wanted.map((ref) => ({
      queryKey: ['discovery', 'sound', ref.platform, ref.id],
      queryFn: () => loaderFor(client!).load(ref),
      enabled: !!client && ref.platform === 'tiktok',
      // A sound's name doesn't change; ones the server couldn't name yet are
      // asked again after this.
      staleTime: 10 * 60_000,
      gcTime: 60 * 60_000,
      retry: false,
    })),
  });
  const byKey = new Map<string, SoundInfo>();
  let loading = false;
  results.forEach((r, i) => {
    if (r.data) byKey.set(soundKey(wanted[i]!), r.data);
    if (r.isLoading) loading = true;
  });
  return { byKey, loading };
}
